// ─── src/utils/listingClassification.js ──────────────────────────────────────
//
// Canonical listing classification rules for the listings table.
//
// DATA CONTRACT:
//   A listing row with category_slug = NULL means "wedding venue".
//   Vendor listings (photographers, planners, florists, etc.) always carry
//   an explicit category_slug. This asymmetry exists because venues were the
//   first listing type added and many were seeded before the column existed.
//
// ALL discovery, search, AI context, maps, recommendations, and analytics
// that read from the listings table must go through these helpers instead of
// writing inline fallback chains. That prevents taxonomy drift as the
// platform grows new surfaces.

export const VENUE_CATEGORY_SLUG = 'wedding-venues';

// All slug values that mean "wedding venue". The categories table uses 'venues'
// as its canonical slug; older seeds and admin-created listings use 'wedding-venues';
// very old listings have category_slug = NULL. All three are equivalent.
const VENUE_SLUGS = new Set(['wedding-venues', 'venues']);

/**
 * Resolve the canonical category slug for a listing row.
 * Null, 'venues', or 'wedding-venues' → 'wedding-venues'.
 *
 * @param {string|null|undefined} raw , The raw category_slug value from the DB row
 * @returns {string}
 */
export function resolveListingCategorySlug(raw) {
  if (!raw || VENUE_SLUGS.has(raw)) return VENUE_CATEGORY_SLUG;
  return raw;
}

/**
 * True if this listing is a venue (including unclassified listings).
 *
 * @param {string|null|undefined} categorySlug
 * @returns {boolean}
 */
export function isVenueListing(categorySlug) {
  return !categorySlug || VENUE_SLUGS.has(categorySlug);
}

// Vendor category aliases: singular/legacy forms that must resolve to the
// canonical plural slug used by categoryRegistry.js / VENDOR_CATEGORIES.
// Deliberately conservative, only unambiguous singular/legacy forms of an
// EXISTING canonical category. Never a fuzzy/adjacent-category guess (e.g.
// 'stylist' is excluded: it could mean wedding-planners or styling-decor,
// and canonicalization must never guess between two real categories).
const VENDOR_CATEGORY_ALIASES = {
  planner:      'wedding-planners',
  planners:     'wedding-planners',
  photographer: 'photographers',
  photography:  'photographers',
  videographer: 'videographers',
  videography:  'videographers',
  florist:      'florists',
  boutique:     'bridal-boutiques',
};

/**
 * Resolve ANY listing's category_slug (venue or vendor) to its canonical
 * form. This is the single entry point every discovery/filter surface
 * should call instead of reading category_slug/categorySlug raw, prevents
 * the same taxonomy-drift bug (e.g. 'planners' vs 'wedding-planners') from
 * resurfacing independently on each page.
 *
 * @param {string|null|undefined} raw
 * @returns {string}
 */
export function canonicalizeCategorySlug(raw) {
  const venueResolved = resolveListingCategorySlug(raw);
  if (venueResolved === VENUE_CATEGORY_SLUG) return venueResolved;
  return VENDOR_CATEGORY_ALIASES[venueResolved] || venueResolved;
}

/**
 * Apply the correct category_slug filter to a Supabase query builder.
 *
 * Venues: OR filter, matches 'wedding-venues', 'venues' (categories table
 *   canonical slug), and NULL (legacy unclassified listings).
 * All other categories: exact equality match.
 *
 * Use this wherever a Supabase query needs to scope to a category.
 *
 * @param {object} query        , Supabase chainable query builder
 * @param {string} categorySlug , The category slug to filter by
 * @returns {object}            , The updated query builder
 */
export function applyListingCategoryFilter(query, categorySlug) {
  if (VENUE_SLUGS.has(categorySlug)) {
    return query.or('category_slug.eq.wedding-venues,category_slug.eq.venues,category_slug.is.null');
  }
  return query.eq('category_slug', categorySlug);
}
